import React, { useContext } from 'react';
import { IViewPortionContext, ViewPortionContext } from '../../viewPortionState/viewPortionContext';
import Text from 'components/Text/Text';
import style from './index.module.scss';
import FullInfoField from 'components/FullInfoField/FullInfoField';
import { priceStrategies } from 'appConstants/dishes';
import { useTranslation } from 'react-i18next';
import { Badge } from 'components/Badge';
import { MenuTranslationForm } from 'components/TranslationForm/MenuTranslationForm';
import { ItemList } from 'components/ViewPage/ItemList';

export const PortionFullInfo: React.FC = () => {
	const { portionFullInfo, setTranslationTab, selectedTranslationTab } =
		useContext<IViewPortionContext>(ViewPortionContext);

	const { t } = useTranslation('form');
	const isDisabledValue = t('isDisabledLabel');
	const priceStrategy = priceStrategies.find((strategy) => strategy.id === portionFullInfo?.priceStrategy);
	const priceStrategyValue = priceStrategy ? t(priceStrategy.name) : '-';

	return (
		<>
			<div className={style.headerContainer}>
				<div className={style.headerRow}>
					<Text
						className={style.headingText}
						translationKey={portionFullInfo?.name}
						size="xl"
						weight="bold"
					/>
					{portionFullInfo?.isCreatedFromPos ? (
						<Badge color="blue">
							<Text
								translationKey="POS"
								asFragment={true}
								data-test={`isCreatedOnPos=${portionFullInfo?.isCreatedFromPos}`}
							/>
						</Badge>
					) : (
						<Badge color="purple">
							<Text
								translationKey="AMS"
								asFragment={true}
								data-test={`isCreatedOnPos=${portionFullInfo?.isCreatedFromPos}`}
							/>
						</Badge>
					)}
				</div>
				<div className={style.headerRow}>
					<Text translationNameSpace="form" translationKey="modifierLabel" theme="neutralLight" size="xs" />
					<Text
						translationNameSpace="form"
						translationKey={portionFullInfo?.modifier.name}
						withTooltip={true}
					/>
				</div>
			</div>
			<div className={style.container} data-test="PortionMainInfo">
				<Text
					translationNameSpace="form"
					translationKey="mainInfoHeading"
					theme="secondary"
					size="l"
					weight="bold"
					display="inline"
				/>
				<div className={style.fieldsWrapper}>
					<div className={style.row}>
						<FullInfoField label="portionNameLabel" value={portionFullInfo?.name} />
						<FullInfoField label="posNameLabel" value={portionFullInfo?.posName} />
					</div>
					<div className={style.row}>
						<FullInfoField label="articleNumberLabel" value={portionFullInfo?.articleNumber} />
						<FullInfoField label="customerLabel" value={portionFullInfo?.customer.name} />
					</div>
					<div className={style.row}>
						<FullInfoField label="restaurantLabel" value={portionFullInfo?.restaurant.name} />
						<FullInfoField label="modifierLabel" value={portionFullInfo?.modifier.name} />
					</div>
					<div className={style.row}>
						<FullInfoField
							label="signedPriceLabel"
							value={
								typeof portionFullInfo?.price === 'number'
									? `${portionFullInfo?.currencySymbol || ''} ${portionFullInfo?.price || 0}`
									: '-'
							}
						/>
						<FullInfoField
							label="signedSupplementPriceLabel"
							value={
								typeof portionFullInfo?.supplementPrice === 'number'
									? `${portionFullInfo?.currencySymbol || ''} ${
											portionFullInfo?.supplementPrice || 0
									  }`
									: '-'
							}
						/>
					</div>
					<div className={style.row}>
						<FullInfoField label="priceStrategyLabel" value={priceStrategyValue} />
					</div>
					<div className={style.row}>
						<FullInfoField label="externalIdLabel" value={portionFullInfo?.externalId} />
						<FullInfoField
							label="isDisabledLabel"
							value={isDisabledValue}
							prefixIcon="asCheckbox"
							prefixIconName={portionFullInfo?.isDisabled ? 'checkMark' : 'crossMark'}
							prefixIconIsChecked={portionFullInfo?.isDisabled}
						/>
					</div>
				</div>
			</div>
			<div className={style.container} data-test="PortionTranslationInfo">
				<Text
					translationNameSpace="form"
					translationKey="translationsHeading"
					theme="secondary"
					size="l"
					weight="bold"
					display="inline"
				/>
				<div className={style.fieldsWrapper}>
					<MenuTranslationForm
						className={style.translationForm}
						setTranslationTab={setTranslationTab}
						isShowLanguageName={false}
						IDSelectedCustomer={portionFullInfo?.customer.id || ''}
						translations={portionFullInfo?.translations || []}
						selectedTranslationTab={selectedTranslationTab}
						nameLabel="portionNameLabel"
						hasDescriptionFullInfoField={false}
					/>
				</div>
			</div>
			<div className={style.container} data-test="PortionParentModGroupsInfo">
				<Text
					translationNameSpace="form"
					translationKey="modifierParentModifierGroupsHeading"
					theme="secondary"
					size="l"
					weight="bold"
					display="inline"
				/>
				{portionFullInfo?.parentModifierGroups.length ? (
					<ItemList
						items={portionFullInfo.parentModifierGroups}
						pathToItem="/menu-v2/modifier-groups/view?modifierGroupID="
					/>
				) : (
					<>
						<Text size="s" translationNameSpace="form" translationKey="noPortionParentModGroupsHeading" />
						<FullInfoField
							containerClassName={style.marginTopZero}
							label="noPortionParentModGroupsDescription"
						/>
					</>
				)}
			</div>
			<div className={style.container} data-test="PortionChildModGroupsInfo">
				<Text
					translationNameSpace="form"
					translationKey="modifierChildModifierGroupsHeading"
					theme="secondary"
					size="l"
					weight="bold"
					display="inline"
				/>
				{portionFullInfo?.childModifierGroups.length ? (
					<ItemList
						items={portionFullInfo.childModifierGroups}
						pathToItem="/menu-v2/modifier-groups/view?modifierGroupID="
					/>
				) : (
					<>
						<Text size="s" translationNameSpace="form" translationKey="noPortionChildModGroupsHeading" />
						<FullInfoField
							containerClassName={style.marginTopZero}
							label="noPortionChildModGroupsDescription"
						/>
					</>
				)}
			</div>
		</>
	);
};
